/**
 * platform/web/src/ui/state/settings.ts
 *
 * The typed settings model (Roadmap §4 screen 06 — Settings).
 *
 * Settings are a small, flat, fully-typed record of user preferences. The hard
 * part of a settings model is robust (DE)SERIALISATION — surviving older/newer
 * persisted shapes, garbage in storage, and partial records — so that is the
 * unit-tested core here (test/settings.test.mjs):
 *   - serialize / deserialize round-trip;
 *   - deserialize fills MISSING keys with defaults (forward/back-compat);
 *   - deserialize IGNORES unknown keys (a newer build wrote extra fields);
 *   - deserialize is TYPE-SAFE (wrong-typed / non-object / invalid JSON -> the
 *     defaults, never a throw) so a corrupt store can never crash the app;
 *   - setOption returns a fresh record (never mutates) and validates the key.
 *
 * Persistence is modelled exactly like the library: an injectable
 * {@link SettingsStorage} seam with an in-memory fake ({@link createMemorySettingsStorage},
 * tested now and the default) plus a real {@link createLocalStorageSettings}
 * adapter (browser-only, build-verified) behind the SAME interface — so the
 * logic is verifiable headlessly today and the real adapter slots in later.
 *
 * §1.6: settings hold ONLY presentation/runtime preferences. No keys, no
 * decryption, nothing about game content.
 *
 * Options exposed reflect things that ACTUALLY exist in the running app:
 *   - keepAspectRatio  the display canvas in main.ts uses CSS `object-fit`
 *                      (contain = letterbox/keep aspect, fill = stretch);
 *   - showEventLog     the boot screen's event-log surface (App.tsx) is real
 *                      and toggleable;
 *   - confirmStop      whether the In-Game "Stop" asks before halting the run;
 *   - muteAudio        whether the audio worker silences playback at runtime
 *                      (main.ts posts a `setMuted` message to the audio worker);
 *   - audioGain        the playback VOLUME multiplier (a number, not a boolean):
 *                      1.0 = unity, 0 = silence, up to {@link MAX_AUDIO_GAIN}.
 *                      main.ts posts a `setGain` message to the worklet, which
 *                      folds it into the tested S16->Float32 convert
 *                      (bindings/audioconvert.ts clampGain/deinterleaveToFloat).
 */

/** Unity gain (no attenuation/amplification): the default volume multiplier.
 *  Kept here so the settings model has the value without importing the worklet's
 *  audioconvert module (which the realtime AudioRing pulls in). */
export const UNITY_AUDIO_GAIN = 1;

/** Upper bound on the persisted volume multiplier. Mirrors audioconvert.MAX_GAIN
 *  (the DSP also clamps), so a slider / corrupt store can never exceed it. */
export const MAX_AUDIO_GAIN = 4;

/** The full, typed settings record. Flat + all-primitive on purpose: trivial to
 *  (de)serialise, diff, and assert on. Mostly booleans; {@link audioGain} is the
 *  one numeric field (the volume multiplier). */
export interface Settings {
  /** Letterbox the display (keep the guest's aspect ratio) vs. stretch to fill. */
  readonly keepAspectRatio: boolean;
  /** Show the boot/event-log panel on the boot screen. */
  readonly showEventLog: boolean;
  /** Ask for confirmation before the In-Game "Stop" halts the run. */
  readonly confirmStop: boolean;
  /** Silence the audio worker's playback (runtime mute). */
  readonly muteAudio: boolean;
  /** Playback volume multiplier in [0, {@link MAX_AUDIO_GAIN}]; 1.0 = unity. */
  readonly audioGain: number;
}

/** A boolean settings key — the keys the {@link setOption}/{@link toggleOption}
 *  reducers and the .tsx checkbox list operate on. The numeric `audioGain` is
 *  handled separately by {@link setGain} (it is not a toggle). */
export type BooleanSettingKey = {
  [K in keyof Settings]: Settings[K] extends boolean ? K : never;
}[keyof Settings];

/** A settings key (any field). */
export type SettingKey = keyof Settings;

/** The factory defaults. Every field has a value, so the app always has a
 *  complete, valid {@link Settings} even with nothing persisted. */
export function defaultSettings(): Settings {
  return {
    keepAspectRatio: true,
    showEventLog: true,
    confirmStop: false,
    muteAudio: false,
    audioGain: UNITY_AUDIO_GAIN,
  };
}

/**
 * Clamp an arbitrary (possibly NaN / out-of-range / non-finite) gain into the
 * safe [0, {@link MAX_AUDIO_GAIN}] volume range. NaN / <=0 / -Infinity collapse
 * to 0 (silence) and +Infinity to the cap, mirroring audioconvert.clampGain so a
 * corrupt persisted value can never produce noise or escape the range. Pure.
 */
export function clampAudioGain(gain: number): number {
  if (!(gain > 0)) return 0; // catches NaN and <=0 -> silence
  if (gain > MAX_AUDIO_GAIN) return MAX_AUDIO_GAIN;
  return gain;
}

/**
 * Format a volume multiplier ({@link Settings.audioGain}) as a human-facing
 * readout for the Settings screen — a percentage AND a decibel figure (the two
 * ways listeners reason about loudness). Pure + tested so the .tsx renders a
 * verified string instead of an inline ad-hoc expression.
 *
 *   - percent = round(gain*100) so unity (1.0) reads "100%", half "50%", the
 *     {@link MAX_AUDIO_GAIN} cap (4.0) "400%";
 *   - dB = 20*log10(gain) (the standard amplitude->dB law), rounded to one
 *     decimal: unity is "0.0 dB" (no shift), half ≈ "-6.0 dB", 4x ≈ "+12.0 dB".
 *     A leading "+" is added for positive gain so attenuation/boost is obvious;
 *   - gain 0 (silence) has no finite dB, so it reads the word "muted" instead of
 *     "-Infinity dB".
 *
 * The input is {@link clampAudioGain}-ed first, so a stray out-of-range value
 * can never produce a nonsense readout. Returns e.g. "100% (0.0 dB)",
 * "50% (-6.0 dB)", "0% (muted)".
 */
export function formatGain(gain: number): string {
  const g = clampAudioGain(gain);
  const percent = `${Math.round(g * 100)}%`;
  if (g === 0) return `${percent} (muted)`;
  const db = 20 * Math.log10(g);
  // One-decimal dB; guard a -0.0 that would print "-0.0" at exactly unity.
  const rounded = Math.round(db * 10) / 10 || 0;
  const sign = rounded > 0 ? "+" : "";
  return `${percent} (${sign}${rounded.toFixed(1)} dB)`;
}

/**
 * Human-facing metadata for each toggle, in display order. Keeping it here (not
 * in the .tsx) means the option list itself is data the screen renders, and the
 * label/description copy is co-located with the model it describes.
 */
export interface SettingMeta {
  readonly key: BooleanSettingKey;
  readonly label: string;
  readonly description: string;
}

export const SETTING_META: readonly SettingMeta[] = [
  {
    key: "keepAspectRatio",
    label: "Keep aspect ratio",
    description: "Letterbox the display to preserve the game's shape instead of stretching to fill the window.",
  },
  {
    key: "showEventLog",
    label: "Show event log",
    description: "Show the boot and runtime event log panel.",
  },
  {
    key: "confirmStop",
    label: "Confirm before stopping",
    description: "Ask before the in-game Stop button halts the running title.",
  },
  {
    key: "muteAudio",
    label: "Mute audio",
    description: "Silence game audio output.",
  },
] as const;

/** True iff `k` is a real {@link SettingKey} (guards untyped input). */
export function isSettingKey(k: string): k is SettingKey {
  return Object.prototype.hasOwnProperty.call(defaultSettings(), k);
}

/** True iff `k` names a BOOLEAN setting (the toggle reducers' domain): a real
 *  key whose default value is a boolean. Excludes the numeric `audioGain`. */
export function isBooleanSettingKey(k: string): k is BooleanSettingKey {
  return isSettingKey(k) && typeof (defaultSettings() as Settings)[k as SettingKey] === "boolean";
}

/**
 * Set one boolean option, returning a fresh {@link Settings}. The input is never
 * mutated; an unknown / non-boolean key returns the same reference (no-op), and
 * setting a key to its current value also returns the same reference so Solid can
 * skip work.
 */
export function setOption(state: Settings, key: BooleanSettingKey, value: boolean): Settings {
  if (!isBooleanSettingKey(key)) return state;
  if (state[key] === value) return state;
  return { ...state, [key]: value };
}

/** Toggle one boolean option (convenience over {@link setOption}). */
export function toggleOption(state: Settings, key: BooleanSettingKey): Settings {
  return setOption(state, key, !state[key]);
}

/**
 * Set the numeric playback volume, returning a fresh {@link Settings}. The value
 * is {@link clampAudioGain}-ed into [0, {@link MAX_AUDIO_GAIN}] (so a bad input
 * can never escape range); setting it to the current (clamped) value returns the
 * SAME reference so Solid / consumers can skip work. Never mutates the input.
 */
export function setGain(state: Settings, gain: number): Settings {
  const next = clampAudioGain(gain);
  if (state.audioGain === next) return state;
  return { ...state, audioGain: next };
}

/**
 * Serialise to a compact JSON string for persistence. Always writes the full,
 * current shape (every field) so a round-trip is lossless.
 */
export function serialize(state: Settings): string {
  const defaults = defaultSettings();
  // Write keys in a stable order (the default key order) so the output is
  // deterministic and diff-friendly. Values are booleans or the numeric gain.
  const out: Record<string, boolean | number> = {};
  for (const key of Object.keys(defaults) as SettingKey[]) {
    out[key] = state[key];
  }
  return JSON.stringify(out);
}

/** Read one boolean field from an untyped record, falling back to a default. */
function readBool(obj: Record<string, unknown>, key: string, fallback: boolean): boolean {
  const v = obj[key];
  return typeof v === "boolean" ? v : fallback;
}

/** Read the numeric gain field from an untyped record, clamped to range. A
 *  non-finite / wrong-typed value falls back to the default; any in-range read is
 *  still {@link clampAudioGain}-ed so a persisted out-of-range value is tamed. */
function readGain(obj: Record<string, unknown>, key: string, fallback: number): number {
  const v = obj[key];
  return typeof v === "number" && Number.isFinite(v) ? clampAudioGain(v) : fallback;
}

/**
 * Parse a persisted string back into a complete, valid {@link Settings}. This is
 * deliberately TOTAL — it never throws:
 *   - invalid JSON, null, or a non-object -> the factory defaults;
 *   - missing fields -> filled from the defaults (forward/back-compat);
 *   - wrong-typed fields -> replaced by the default for that field;
 *   - unknown extra fields -> ignored.
 * So a corrupt or stale store can never crash the app or smuggle junk in.
 */
export function deserialize(raw: string | null | undefined): Settings {
  const defaults = defaultSettings();
  if (raw === null || raw === undefined) return defaults;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return defaults;
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return defaults;
  }
  const obj = parsed as Record<string, unknown>;
  return {
    keepAspectRatio: readBool(obj, "keepAspectRatio", defaults.keepAspectRatio),
    showEventLog: readBool(obj, "showEventLog", defaults.showEventLog),
    confirmStop: readBool(obj, "confirmStop", defaults.confirmStop),
    muteAudio: readBool(obj, "muteAudio", defaults.muteAudio),
    audioGain: readGain(obj, "audioGain", defaults.audioGain),
  };
}

/**
 * The persistence seam. Synchronous + tiny, mirroring {@link LibraryStorage}: the
 * in-memory fake is trivial and the real localStorage adapter implements the
 * same shape. The model never calls these itself; they are the wiring the app
 * uses to load on start and save on change.
 */
export interface SettingsStorage {
  load(): Settings;
  save(state: Settings): void;
}

/**
 * The in-memory storage fake: the default adapter and the one tests drive, so
 * the settings logic is fully verifiable without a browser. load() returns the
 * defaults until something is saved; values round-trip through serialize /
 * deserialize so the fake exercises the same (de)serialisation path the real
 * adapter does.
 */
export function createMemorySettingsStorage(seed?: Settings): SettingsStorage {
  let raw: string | null = seed ? serialize(seed) : null;
  return {
    load(): Settings {
      return deserialize(raw);
    },
    save(state: Settings): void {
      raw = serialize(state);
    },
  };
}

/** A minimal localStorage-shaped surface, so the adapter is typeable/testable
 *  without the DOM lib types and a fake can stand in for it. */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The storage key the real adapter reads/writes under. */
export const SETTINGS_STORAGE_KEY = "voland.settings.v1";

/**
 * The real persistence adapter over a {@link KeyValueStore} (Window.localStorage
 * in the browser). It is build-verified and shares the SAME tested
 * (de)serialisation as the in-memory fake, so its behaviour is covered by the
 * deserialize/serialize tests. All access is wrapped so a throwing/blocked store
 * (private mode, quota) degrades to in-memory behaviour rather than crashing.
 */
export function createLocalStorageSettings(
  store: KeyValueStore,
  key: string = SETTINGS_STORAGE_KEY,
): SettingsStorage {
  return {
    load(): Settings {
      try {
        return deserialize(store.getItem(key));
      } catch {
        return defaultSettings();
      }
    },
    save(state: Settings): void {
      try {
        store.setItem(key, serialize(state));
      } catch {
        /* storage unavailable (private mode / quota) — drop silently. */
      }
    },
  };
}
